import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";

const Lbl = ({ children }) => <label className="text-[11px] tracking-[0.2em] uppercase">{children}</label>;

export const BespokeFields = ({ p, update, cat }) => {
  const [finishes, setFinishes] = useState([]);
  const [len, setLen] = useState("");
  useEffect(() => { api.get("/settings").then((r) => setFinishes(r.data.finishes || [])); }, []);
  const b = { standard_config: "", standard_length_ft: null, length_options: [], prices: {}, ...(p.bespoke || {}) };
  const set = (patch) => update({ bespoke: { ...b, ...patch } });
  const num = (v) => (v === "" ? null : Number(v));
  const addLen = () => {
    const v = parseFloat(len);
    if (v > 0 && !b.length_options.includes(v)) set({ length_options: [...b.length_options, v].sort((x, y) => x - y) });
    setLen("");
  };

  return (
    <section className="bg-white border border-[var(--line)] p-6 space-y-6" data-testid="bespoke-fields">
      <div>
        <h3 className="text-base md:text-lg font-bold">Bespoke data sheet</h3>
        <p className="text-xs text-[var(--muted-ink)] mt-1">This catalog uses the Bespoke style. Fabric &amp; leather options, rates and notes come from <Link to="/settings" className="underline">Settings</Link>.</p>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <div><Lbl>Standard configuration</Lbl>
          <Select value={b.standard_config || undefined} onValueChange={(v) => set({ standard_config: v })}>
            <SelectTrigger className="mt-1" data-testid="bespoke-config-select"><SelectValue placeholder="Pick one" /></SelectTrigger>
            <SelectContent>{(cat.configurations || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Lbl>Standard length (ft)</Lbl>
          <Input type="number" step="0.5" value={b.standard_length_ft ?? ""} onChange={(e) => set({ standard_length_ft: num(e.target.value) })} className="mt-1" data-testid="bespoke-length-input" /></div>
        <div><Lbl>Customization lengths (ft)</Lbl>
          <div className="flex gap-2 mt-1">
            <Input type="number" step="0.5" value={len} onChange={(e) => setLen(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addLen())} placeholder="e.g. 6" data-testid="bespoke-length-option-input" />
            <button onClick={addLen} className="border border-[var(--line)] px-3 text-sm" data-testid="bespoke-length-option-add">Add</button>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2" data-testid="bespoke-length-options">
            {b.length_options.map((o) => (
              <span key={o} className="flex items-center gap-1 bg-[var(--paper)] px-2 py-1 text-xs">{o} ft
                <button onClick={() => set({ length_options: b.length_options.filter((x) => x !== o) })} data-testid={`bespoke-length-remove-${o}`}><X size={11} /></button></span>
            ))}
          </div></div>
      </div>
      <div>
        <Lbl>Price per option {b.standard_config ? `· ${b.standard_config}` : ""}{b.standard_length_ft ? ` (${b.standard_length_ft} ft)` : ""} · ₹</Lbl>
        <div className="grid sm:grid-cols-2 gap-3 mt-2">
          {finishes.map((f, i) => (
            <div key={f.name} className="flex items-center gap-2 border border-[var(--line)] pl-3">
              <span className="text-sm flex-1 truncate" title={f.name}>{f.name}</span>
              <Input type="number" value={b.prices[f.name] ?? ""} placeholder="On request" className="w-32 shrink-0 border-0 border-l rounded-none"
                onChange={(e) => set({ prices: { ...b.prices, [f.name]: num(e.target.value) } })} data-testid={`bespoke-price-${i}`} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
